import { useRef, useState } from 'react';
import {
  AcademicCapIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth, useUser } from '@clerk/react';
import { WithTooltip } from '../../../components/WithTooltip';
import { Mixpanel } from '../../../services/Mixpanel';
import { getCategorizedSubjects } from '../../../utils/skills';
import {
  CURRICULUM_SUBJECTS,
  buildCurriculumRequestUrl,
  lessonLabel,
  searchCurricula,
} from '../../../utils/curriculum';
import { useCurricula } from '../../../hooks/useCurricula';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';
import { useDropdownPosition } from '../hooks/useDropdownPosition';
import { isHoveringPointer } from '../utils/isHoveringPointer';
import { useUnlimitedContext } from '../contexts/UnlimitedContext';
import { useUnlimitedModals } from '../contexts/UnlimitedModalsContext';
import PremiumIconCrown from '../../../components/PremiumIconCrown';
import ExportUpsellCard from '../../../components/ExportUpsellCard';
import RequestCurriculumModal from '../../../components/modals/RequestCurriculumModal';
import { useExportGate, useTrialBannerViewed } from '../../../utils/exportTrialState';

/**
 * Dropdown menu for aligning generated content to academic standards or to a
 * curriculum's lessons. Reads selection state from UnlimitedContext and opens
 * modals via UnlimitedModalsContext.
 *
 * With the `align-to-curriculum` flag on, the root lists Standards and
 * Curriculum as parent rows, each opening its own side flyout — the same nested
 * pattern AdaptMenu uses (see "nested list" notes below). Flag off, the root is
 * the standards subject list exactly as before, with no nesting at all.
 */
// tooltipPosition: 'top' under an input at the bottom of the screen (Courses).
export const AlignMenu = ({ tooltipPosition = 'bottom' } = {}) => {
  const { standards, curriculum, requireAuth, requireAuthForMenus, trackingSource, flags } =
    useUnlimitedContext();
  // Another surface (Courses) tags its events; the create screen's stay exactly as they were.
  const track = (name, props) =>
    Mixpanel.track(name, trackingSource ? { ...props, source: trackingSource } : props);
  const {
    showStandardsSubmenu,
    setShowStandardsSubmenu,
    hasSelectedStandards,
    languageStandards,
    setLanguageStandards,
    contentStandards,
    setContentStandards,
  } = standards;
  const modals = useUnlimitedModals();
  // Wait for LD to identify the visitor before letting the flag reshape the menu.
  // withLDProvider evaluates against an anonymous context first (see the repo
  // CLAUDE.md), so reading the flag bare would flip the menu's shape mid-session
  // and attribute `Unlimited Align Section Selected` to the wrong context — the
  // measurement the rollout depends on.
  const curriculumEnabled = !!flags.alignToCurriculum && !!flags.isLDidentified;

  // One boolean per branch, mirroring AdaptMenu: the parent rows stay mounted and the
  // branch opens beside them rather than replacing the panel body.
  const [showStandardsFlyout, setShowStandardsFlyout] = useState(false);
  const [showCurriculumFlyout, setShowCurriculumFlyout] = useState(false);
  const curricula = useCurricula(curriculumEnabled, showCurriculumFlyout);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [curriculumSearch, setCurriculumSearch] = useState('');
  // Names often differ only at the end, so a truncated one gets its full text on hover.
  const [truncatedCurriculumId, setTruncatedCurriculumId] = useState(null);
  const [submenuMobileStyle, setSubmenuMobileStyle] = useState(null);
  const [requestedCurriculum, setRequestedCurriculum] = useState(null);

  const { diffitUser, selectedOrg } = useDiffitUserContext();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { menuRef, dropUp, leftOffset, maxHeight, recalculate } = useDropdownPosition({
    dropdownWidth: 256,
    // The flyout escapes the panel's maxHeight clamp, so the drop-direction threshold
    // has to cover it, not just the two-row panel. 320/256 is Adapt's pairing.
    minSpaceBelow: curriculumEnabled ? 320 : 280,
    // Flag off the subject list IS the panel and needs the cap; flag on it is two rows
    // plus a capped flyout, so a cap here would only squeeze the chips (as in Adapt).
    maxMenuHeight: curriculumEnabled ? undefined : 256,
  });
  // Flyouts open from the panel's top edge, or from its bottom edge when the menu opens
  // upward (an input at the bottom of the screen), so they stay on screen.
  const flyoutEdge = dropUp ? 'bottom-0' : 'md:top-0';

  // bts-2026-freemium: aligning to specific standards or a curriculum is premium.
  // The crown + upsell card show for free + trial (gateActive requires flag on + LD
  // identified); paid and flag-off render exactly as before. Entries stay clickable —
  // the modals carry their own gate. See exportTrialState.js (tier-generic despite
  // the name).
  const { tier, gateActive, showPremiumCrowns, showTrialCountdown } = useExportGate();
  const standardsLocked = gateActive && tier === 'free';
  const showAlignUpsell = showTrialCountdown || standardsLocked;
  const trackEvent = (name, props) => track(name, props);
  useTrialBannerViewed(trackEvent, showTrialCountdown && showStandardsSubmenu, 'align_menu');

  const alignLabel = curriculumEnabled ? 'Align to standards and curriculum' : 'Align to standards';

  const closeAllSubmenus = () => {
    setShowStandardsFlyout(false);
    setShowCurriculumFlyout(false);
    setSubjectSearch('');
    setCurriculumSearch('');
  };

  const closeMenu = () => {
    setShowStandardsSubmenu(false);
    closeAllSubmenus();
  };

  // Below md there is no room beside the panel, so anchor the flyout to the viewport's
  // right edge. Mirrors AttachMenu's local-state copy of adaptSubmenuMobileStyle.
  const computeSubmenuMobileStyle = () => {
    const rect = menuRef.current?.getBoundingClientRect();
    if (!rect || window.innerWidth >= 848) {
      setSubmenuMobileStyle(null);
      return;
    }
    const dropdownWidth = 256;
    const overflow = rect.left + dropdownWidth - window.innerWidth + 16;
    const freshLeftOffset = overflow > 0 ? -overflow : 0;
    const parentRight = rect.left + freshLeftOffset + dropdownWidth;
    const spaceRight = Math.max(0, window.innerWidth - 16 - parentRight);
    setSubmenuMobileStyle({ right: -spaceRight, width: 224 + spaceRight });
  };

  const openSection = (section) => {
    setShowStandardsFlyout(section === 'standards');
    setShowCurriculumFlyout(section === 'curriculum');
    if (section !== 'standards') setSubjectSearch('');
    if (section !== 'curriculum') setCurriculumSearch('');
  };

  // Gates the align-to-curriculum rollout, so it must keep meaning "engaged this
  // section". Hovering the row does not count — Standards is the first row under the
  // trigger, so every pointer crosses it. Clicking alone does not either — hover has
  // already opened the section, so the click closes it. Report only on gestures
  // transit cannot produce: explicit activation, or reaching the flyout to the right.
  // `trigger` because the two gestures are not the same signal and neither matches the
  // pre-flag semantics; whoever owns the rollout can segment rather than having to pick
  // a definition up front.
  const sectionsReported = useRef(new Set());
  const reportSection = (section, trigger) => {
    if (!section || sectionsReported.current.has(section)) return;
    sectionsReported.current.add(section);
    track('Unlimited Align Section Selected', { section, trigger });
  };

  const activateSection = (section) => {
    openSection(section);
    reportSection(section, 'activate'); // no-op when collapsing (section is null)
  };

  const selectedChips = [
    ...languageStandards.map((s) => ({
      key: `language-${s.id}`,
      label: s.name || s.id,
      onRemove: () => {
        setLanguageStandards((prev) => prev.filter((x) => x.id !== s.id));
        track('Unlimited Standard Removed', { standard_id: s.id, standard_name: s.name });
      },
    })),
    ...contentStandards.map((s) => ({
      key: `content-${s.id}`,
      label: s.name || s.id,
      onRemove: () => {
        setContentStandards((prev) => prev.filter((x) => x.id !== s.id));
        track('Unlimited Standard Removed', { standard_id: s.id, standard_name: s.name });
      },
    })),
    ...(curriculumEnabled ? curriculum.selectedLessons : []).map((lesson) => ({
      key: `curriculum-${lesson.id}`,
      label: lessonLabel(lesson),
      onRemove: () => {
        curriculum.removeLesson(lesson.id);
        track('Unlimited Curriculum Lesson Removed', { lesson_id: lesson.id });
      },
    })),
  ];
  const hasSelection = hasSelectedStandards || (curriculumEnabled && curriculum.hasSelection);

  // A count, not the value: the Selected strip below already names every choice, and a
  // name overflows the pill's 7rem and truncates the row's own label instead.
  const pillLabel = (count) => (count === 0 ? 'Auto' : `${count} chosen`);
  const standardsCount = languageStandards.length + contentStandards.length;
  const curriculumCount = curriculumEnabled ? curriculum.selectedLessons.length : 0;

  // Clearing a whole dimension emits the same per-item event the chips do, so the
  // existing Standard Removed / Lesson Removed funnels stay intact.
  const clearStandards = () => {
    [...languageStandards, ...contentStandards].forEach((s) =>
      track('Unlimited Standard Removed', { standard_id: s.id, standard_name: s.name })
    );
    setLanguageStandards([]);
    setContentStandards([]);
  };
  const clearCurriculum = () => {
    curriculum.selectedLessons.forEach((lesson) => {
      curriculum.removeLesson(lesson.id);
      track('Unlimited Curriculum Lesson Removed', { lesson_id: lesson.id });
    });
  };

  const rowClasses =
    'group w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm';
  const flyoutClasses = `absolute top-auto ${submenuMobileStyle ? '' : 'right-0 w-56'} md:right-auto md:left-full ${flyoutEdge} md:-ml-2 md:w-64 overflow-y-auto overscroll-contain scrollbar-dropdown bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 z-50`;

  // A focusable div, not a button: it contains the pill's clear-X, and a button may not
  // nest in a button. role/tabIndex/keydown keep it keyboard-reachable, which Adapt's
  // plain divs are not.
  const renderParentRow = ({ section, name, open, pillText, pillActive, onClear, clearLabel }) => (
    <div
      key={section}
      role='button'
      tabIndex={0}
      aria-haspopup='true'
      aria-expanded={open}
      onPointerEnter={(e) => {
        if (isHoveringPointer(e)) openSection(section);
      }}
      onClick={(e) => {
        e.stopPropagation();
        activateSection(open ? null : section);
      }}
      onKeyDown={(e) => {
        // Row only. Enter/Space are also the nested clear-X's activation, and
        // preventDefault here would cancel it — toggling the flyout instead of clearing.
        if (e.target !== e.currentTarget) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        activateSection(open ? null : section);
      }}
      className={`${rowClasses} cursor-default`}>
      <span className='truncate'>{name}</span>
      <span className='flex items-center gap-1.5 min-w-0'>
        {/* Persistent, like Adapt's MTSS row: a hover-revealed crown on a row that also
            opens on hover would only ever appear alongside its own flyout. */}
        {showPremiumCrowns && (
          <PremiumIconCrown
            className='h-3.5 w-3.5'
            wrapperClassName='flex-shrink-0'
            toolTipProps={{ text: undefined }}
          />
        )}
        <span
          className={`${pillActive ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'} inline-flex items-center min-w-0 px-2 py-0.5 rounded-full text-xs max-w-[7rem]`}>
          <span className='truncate'>{pillText}</span>
          {pillActive && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className='-my-0.5 -mr-2 ml-0.5 flex items-center justify-center self-stretch w-5 shrink-0 rounded-full hover:bg-emerald-200 transition-colors'
              aria-label={clearLabel}>
              <XMarkIcon className='w-2.5 h-2.5' />
            </button>
          )}
        </span>
        <ChevronRightIcon className='w-3.5 h-3.5 text-gray-400 shrink-0' />
      </span>
    </div>
  );

  const renderSubjectRows = ({ search = '' } = {}) => {
    const query = search.trim().toLowerCase();
    // While searching, drop the Recommended / All Standards captions — a caption
    // over a filtered subset misreports what the group contains.
    const items = getCategorizedSubjects(diffitUser, isSignedIn).filter((item) =>
      query ? !item.isCategory && item.name.toLowerCase().includes(query) : true
    );
    if (query && items.length === 0) {
      return (
        <div className='px-3 py-2 text-sm text-gray-500'>
          <span>No standards match “{search.trim()}”</span>
        </div>
      );
    }
    return items.map((item, index) =>
      item.isCategory ? (
        <div
          key={`category-${index}`}
          className={`flex items-center justify-between gap-2 px-3 py-1.5 text-xs font-normal text-gray-600 uppercase tracking-wide${index > 0 ? ' mt-3' : ' mt-1'}`}>
          <span>{item.name}</span>
          {showPremiumCrowns && (
            <PremiumIconCrown
              className='h-3.5 w-3.5'
              wrapperClassName='flex-shrink-0'
              toolTipProps={{ text: undefined }}
            />
          )}
        </div>
      ) : (
        <button
          key={item.value}
          onClick={() => {
            modals.setStandardsSubject(item.value);
            modals.setShowStandardsModal(true);
            closeMenu();
            track('Unlimited Standards Subject Selected', { subject: item.value });
          }}
          className={rowClasses}>
          <span className='truncate'>{item.name}</span>
          {showPremiumCrowns && (
            <PremiumIconCrown
              className='h-3.5 w-3.5'
              wrapperClassName='flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100'
              toolTipProps={{ text: undefined }}
            />
          )}
        </button>
      )
    );
  };

  const renderCurriculumRow = (item) => (
    <button
      key={item.id}
      onClick={() => {
        if (item.isLocked) {
          closeMenu();
          track('Unlimited Locked Curriculum Selected', {
            curriculum_id: item.id,
            curriculum_source: item.source,
          });
          // The request emails the teacher's admin, so a signed-out visitor signs in first.
          requireAuth(() => setRequestedCurriculum(item));
          return;
        }
        modals.setCurriculumId(item.id);
        modals.setShowCurriculumModal(true);
        closeMenu();
        track('Unlimited Curriculum Selected From Menu', {
          curriculum_id: item.id,
          curriculum_source: item.source,
        });
      }}
      className={rowClasses}>
      <span className='flex flex-col min-w-0'>
        <WithTooltip
          text={truncatedCurriculumId === item.id ? item.displayName : ''}
          position='top'
          usePortal
          zIndex={60}>
          <span
            className='truncate'
            onMouseEnter={(e) =>
              setTruncatedCurriculumId(
                e.currentTarget.scrollWidth > e.currentTarget.clientWidth ? item.id : null
              )
            }>
            {item.displayName}
          </span>
        </WithTooltip>
        <span className='truncate text-xs text-gray-500'>{item.description}</span>
      </span>
      {item.isLocked ? (
        <LockClosedIcon className='h-3.5 w-3.5 flex-shrink-0 text-gray-400' aria-label='Locked' />
      ) : (
        showPremiumCrowns && (
          <PremiumIconCrown
            className='h-3.5 w-3.5'
            wrapperClassName='flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100'
            toolTipProps={{ text: undefined }}
          />
        )
      )}
    </button>
  );

  // Grouped by subject; while searching the captions drop, as in the standards list.
  const renderCurriculumRows = ({ search = '' } = {}) => {
    const query = search.trim();
    const matches = searchCurricula(curricula, query);
    const groups = query
      ? [{ subject: null, items: matches }]
      : CURRICULUM_SUBJECTS.map((subject) => ({
          subject,
          items: matches.filter((item) => item.subject === subject),
        })).filter((group) => group.items.length);
    return [
      ...(query && !matches.length
        ? [
            <div key='no-curricula' className='px-3 py-2 text-sm text-gray-500'>
              <span>No curricula match “{query}”</span>
            </div>,
          ]
        : []),
      ...groups.flatMap((group, index) => [
        ...(group.subject
          ? [
              <div
                key={`subject-${group.subject}`}
                className={`px-3 py-1.5 text-xs font-normal text-gray-600 uppercase tracking-wide${index > 0 ? ' mt-3' : ' mt-1'}`}>
                <span>{group.subject}</span>
              </div>,
            ]
          : []),
        ...group.items.map(renderCurriculumRow),
      ]),
      // Teaching something we don't publish yet is the common case, so the ask
      // sits right under the list rather than behind an empty state.
      <a
        key='request-curriculum'
        href={buildCurriculumRequestUrl({ user, diffitUser, selectedOrg })}
        target='_blank'
        rel='noreferrer'
        onClick={() => {
          closeMenu();
          track('Unlimited Curriculum Requested');
        }}
        className={`${rowClasses} mt-1 border-t border-gray-100 pt-2.5 rounded-t-none`}>
        <span className='flex flex-col min-w-0'>
          <span className='truncate'>Request your curriculum</span>
          <span className='truncate text-xs text-gray-500'>More options coming soon</span>
        </span>
        <ArrowTopRightOnSquareIcon className='h-4 w-4 flex-shrink-0 text-gray-400' />
      </a>,
    ];
  };

  // Must render BELOW the parent rows. Flyouts pin to the panel's top edge and do not
  // track their row, so a variable-height block above the rows — one chip per selection
  // — pushes them arbitrarily far from the flyout they open.
  const selectedStrip = hasSelection ? (
    // Moving off the parent rows onto other panel content collapses the open flyout —
    // Adapt does this from its info-note row, Attach from every non-submenu row.
    <div className='pt-2 pb-4 mb-1' onMouseEnter={closeAllSubmenus}>
      <div className='px-3 pb-3 text-xs font-normal text-gray-600 uppercase tracking-wide'>
        {curriculumEnabled ? 'Selected' : 'Selected Standards'}
      </div>
      <div className='flex flex-col gap-2.5'>
        {selectedChips.map((chip) => (
          <div
            key={chip.key}
            className='w-full h-7 flex items-center justify-between pl-3 pr-0.5 bg-emerald-50 text-emerald-800 rounded-full text-xs'>
            <span className='truncate'>{chip.label}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                chip.onRemove();
              }}
              className='flex items-center justify-center w-6 h-6 my-auto rounded-full hover:bg-emerald-100 hover:text-emerald-900 transition-colors'
              aria-label={`Remove ${chip.label}`}>
              <XMarkIcon className='w-3 h-3' />
            </button>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className='relative' ref={menuRef}>
      <WithTooltip
        text={showStandardsSubmenu ? '' : alignLabel}
        position={tooltipPosition}
        anchor='center'
        size='xs'
        showArrow>
        <button
          onClick={() => {
            requireAuthForMenus(() => {
              if (!showStandardsSubmenu) {
                track('Unlimited Align Standards Clicked');
                sectionsReported.current.clear();
                recalculate();
                computeSubmenuMobileStyle();
              } else {
                closeAllSubmenus();
              }
              setShowStandardsSubmenu((prev) => !prev);
            });
          }}
          className={`group flex items-center gap-1.5 py-2 px-3 rounded-xl transition-colors md:min-w-[5rem] max-w-[10rem] md:max-w-[14rem] ${
            hasSelection
              ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-100'
              : 'text-gray-600 hover:bg-gray-100 border border-transparent'
          }`}
          aria-label={alignLabel}>
          <AcademicCapIcon className='w-5 h-5 flex-shrink-0' />
          <span className='text-sm truncate'>
            <span className='hidden md:inline'>Align</span>
            {hasSelection && (
              <span className='hidden md:inline'>
                : {selectedChips.map((chip) => chip.label.slice(0, 16)).join(', ')}
              </span>
            )}
          </span>
        </button>
      </WithTooltip>
      {showStandardsSubmenu && (
        <>
          <div className='fixed inset-0 z-40' onClick={closeMenu} />
          <div
            className={`absolute w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 flex flex-col ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
            style={{ left: leftOffset, maxHeight }}
            onClick={closeAllSubmenus}>
            {/* The flyouts below are siblings of this wrapper, never children:
                overflow-y-auto would force overflow-x and clip a left-full submenu away
                with no error. Unlike Adapt the p-1.5 stays here rather than on the panel,
                so the flag-off subtree renders exactly as it ships; md:top-0 resolves
                against the panel's padding box either way, so the flyouts don't care. */}
            <div className='overflow-y-auto overscroll-contain scrollbar-dropdown p-1.5 min-h-0'>
              {!curriculumEnabled && selectedStrip}
              {curriculumEnabled
                ? [
                    renderParentRow({
                      section: 'standards',
                      name: 'Standards',
                      open: showStandardsFlyout,
                      pillText: pillLabel(standardsCount),
                      pillActive: hasSelectedStandards,
                      onClear: clearStandards,
                      clearLabel: 'Clear selected standards',
                    }),
                    renderParentRow({
                      section: 'curriculum',
                      name: 'Curriculum',
                      open: showCurriculumFlyout,
                      pillText: pillLabel(curriculumCount),
                      pillActive: curriculum.hasSelection,
                      onClear: clearCurriculum,
                      clearLabel: 'Clear selected curriculum lessons',
                    }),
                  ]
                : renderSubjectRows()}
              {curriculumEnabled && selectedStrip}
            </div>
            {/* Searchable, as Adapt's language flyout is — that is how a long list stays
                usable inside max-h-64. pt-0 here + pt-1.5 on the sticky bar is Adapt's
                split, so the search keeps its inset once the list scrolls under it. */}
            {curriculumEnabled && showStandardsFlyout && (
              <div
                className={`${flyoutClasses} max-h-64 pt-0`}
                style={submenuMobileStyle || undefined}
                onMouseEnter={() => reportSection('standards', 'flyout_reached')}
                onClick={(e) => e.stopPropagation()}>
                <div className='sticky top-0 bg-white pt-1.5 pb-1.5 px-0 z-10'>
                  <input
                    type='text'
                    value={subjectSearch}
                    onChange={(e) => setSubjectSearch(e.target.value)}
                    placeholder='Search standards...'
                    className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-0 focus:border-gray-900'
                    // Type-to-filter on open, as Adapt's language search does. Desktop
                    // only: below md this raises the keyboard over the list it filters.
                    autoFocus={window.innerWidth >= 848}
                  />
                </div>
                {renderSubjectRows({ search: subjectSearch })}
              </div>
            )}
            {curriculumEnabled && showCurriculumFlyout && (
              <div
                className={`${flyoutClasses} max-h-64 pt-0`}
                style={submenuMobileStyle || undefined}
                onMouseEnter={() => reportSection('curriculum', 'flyout_reached')}
                onClick={(e) => e.stopPropagation()}>
                <div className='sticky top-0 bg-white pt-1.5 pb-1.5 px-0 z-10'>
                  <input
                    type='text'
                    value={curriculumSearch}
                    onChange={(e) => setCurriculumSearch(e.target.value)}
                    placeholder='Search curricula...'
                    className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-0 focus:border-gray-900'
                    autoFocus={window.innerWidth >= 848}
                  />
                </div>
                {renderCurriculumRows({ search: curriculumSearch })}
              </div>
            )}
            {showAlignUpsell && (
              <div
                className='p-1.5 border-t border-gray-100 flex-shrink-0'
                onMouseEnter={closeAllSubmenus}>
                <ExportUpsellCard
                  mode={showTrialCountdown ? 'trial' : 'free'}
                  location='align_menu'
                  trackEvent={trackEvent}
                  freeHeading='Need to align to specific standards?'
                />
              </div>
            )}
          </div>
        </>
      )}
      <RequestCurriculumModal
        curriculum={requestedCurriculum}
        onClose={() => setRequestedCurriculum(null)}
      />
    </div>
  );
};
